import React, {
  useEffect, useMemo, useState, useCallback,
} from 'react';
import * as DOMPurify from 'dompurify';
import _ from 'lodash';
import { Button } from '@material-ui/core';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, Link } from 'react-router-dom';
import { Skeleton } from '@material-ui/lab';
import config from '@/configs/config';
import Navmenu from '../navmenu';
import Alert from '../alert';
import { useProductDetailsContext } from '../../contexts/productDetails';
import { useProductsContext } from '../../contexts/products';
import { useProviderTagsContext } from '../../contexts/providerTags';
import { useProvidersContext } from '../../contexts/providers';
import { useProviderDetailsContext } from '../../contexts/providerDetails';
import {
  findProviders, findProductDetails, findProductStock, findProviderTags,
} from '../../api/ecDataQueryApi';

import { useProgressContext } from '../../../preorder/contexts/useProgressContext';
import { useShoppingCartContext } from '../../contexts/shoppingCart';
import ImageSet from './ImageSet';

const ProductDetailPageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  position: relative;
  padding: 10px;
  text-align: center;
`;

const TagSetContainer = styled.div`
  width: 100%;
  height: 30px;
  text-align: left;
  border-bottom: 1px solid black;
  margin: 20px 0px;
  & * {
    color: black;
  }
`;

const ImageSetContainer = styled.div`
  width: 55%;
  min-width: 360px;
  display: inline-block;
  vertical-align: top;
`;

const DetailContainer = styled.div`
  width: calc(45% - 40px);
  min-width: 320px;
  display: inline-block;
  vertical-align: top;
  background-color: #EEE;
  padding: 20px;
  margin: 40px 20px;
  text-align: left;
`;
const ProviderName = styled.div`
  font-size: 20px;
  display: block;
`;
const Name = styled.div`
  font-size: 24px;
  display: block;
`;
const Price = styled.div`
  display: inline-block;
  font-size: larger;
`;
const PriceLineThrough = styled(Price)`
  display: inline-block;
  text-decoration: line-through;
  font-size: medium;
`;
const SellPrice = styled.div`
  color: red;
  font-size: larger;
  display: inline-block;
  margin-left: 10px;
`;

const SpecContainer = styled.table`
  & td{
    padding: 5px;
    vertical-align: top;
  }
  & td:first-child{
    white-space: nowrap;
  }
`;

const SpecOption = styled.div`
  display: inline-block;
  cursor: pointer;
  padding: 0px 10px;
  margin-right: 5px;
  height: 30px;
  line-height: 30px;
  border: 1px solid #AAA;
  color: black;
  ${
  (props) => (props.selected ? (
    'background-color: #AAA;'
  ) : (
    `
        background-color: white;
        &:hover{
          opacity: 0.6;
        }
      `
  ))
}
`;

const Select = styled.select`
  width: calc(100% - 10px);
  padding: 0px 10px;
  height: 30px;
  line-height: 30px;
  border: 1px solid #AAA;
  color: black;
`;

const AmountSelector = styled.div`
  border: 1px solid grey;
  display: inline-block;
  white-space: nowrap;
  & div{
    text-align: center;
    height: 25px;
    line-height: 25px;
    display: inline-block;
    vertical-align: top;
    cursor: pointer;
    user-select: none;
  }
  & #decrease, & #increase{
    background-color: #CCC;
    width: 50px;
    &:hover{
      opacity: 0.6;
    }
  }
  & #amount{
    background-color: white;
    width: 100px;
  }
`;

const AddToCartLink = styled.div`
  color: black;
  margin-left: 10px;
  display: inline-block;
  text-decoration: underline;
  cursor: pointer;
  user-select: none;
  &:hover{
    opacity: 0.6;
  }
`;

const CheckoutButton = styled.div`
  display: block;
  background-color: black;
  color: white;
  border-radius: 5px;
  width: 100%;
  text-align: center;
  height: 40px;
  line-height: 40px;
  margin-top: 20px;
  cursor: pointer;
  user-select: none;
  &:hover{
    opacity: 0.6;
  }
`;

const DescriptionTagContainer = styled.div`
  white-space: nowrap;
  overflow-x: auto;
  width: 100%;
  position: relative;
  text-align: left;
  padding-left: 10px;
  &:after{
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background-color: black;
  }
`;

const DescriptionTag = styled.div`
  display: inline-block;
  padding: 10px;
  color: white;
  margin: 15px 5px 0px 5px;
  cursor: pointer;
  user-select: none;
  ${
  (props) => (props.selected ? (
    'background-color: black;'
  ) : (
    `background-color: #AAA;
        &:hover{
          opacity: 0.6;
        }
      `
  ))
}
`;

const DescriptionContainer = styled.div`
  display: block;
  width: 100%;
  padding: 10px;
  text-align: left;
  margin: 10px;
`;

export default ({ match }) => {
  const { t } = useTranslation();
  const { start: startProgress, end: endProgress, isFinished: isPrepared } = useProgressContext();
  const history = useHistory();
  const { addItem } = useShoppingCartContext();
  const {
    providerTags: providerTagsCache,
    addProviderTags,
  } = useProviderTagsContext();
  const {
    productDetails: productDetailsCache,
    addProductDetails,
  } = useProductDetailsContext();
  const { products: productsCache } = useProductsContext();
  const {
    providers: providersCache,
    addProviders,
  } = useProvidersContext();
  const {
    providerDetails: providerDetailsCache,
  } = useProviderDetailsContext();

  const [alert, setAlert] = useState(undefined);
  const [selectedDescriptionIndex, setSelectedDescriptionIndex] = useState(0);
  const [selectedSpecs, setSelectedSpecs] = useState({});
  const [selectedAmount, setSelectedAmount] = useState(1);

  const productKey = useMemo(() => _.get(match, ['params', 'productId'], null), [match]);
  const productData = useMemo(
    () => _.get(productsCache, [productKey]),
    [productKey, productsCache]
  );
  const productDetailData = useMemo(
    () => _.get(productDetailsCache, [productKey]),
    [productKey, productDetailsCache]
  );

  const {
    name,
    defaultTag,
    providerKey,
    price,
    sellPrice,
    specs,
    specSellPrices,
    specPrices,
    images,
    descriptions,
  } = useMemo(() => {
    const target = {
      ...(productData || {}),
      ...(productDetailData || {}),
    };
    return {
      name: target.name || '',
      defaultTag: _.get(target, ['tags', 0], null),
      providerKey: target.providerKey || '',
      price: target.price || 0,
      sellPrice: target.sellPrice || 0,
      specs: target.specs || [],
      specSellPrices: target.specSellPrices || {},
      specPrices: target.specPrices || {},
      images: target.images || [],
      descriptions: target.descriptions || [],
    };
  }, [productDetailData, productData]);

  const provider = useMemo(() => _.get(providersCache, [providerKey, 'name'], null)
    || _.get(providerDetailsCache, [providerKey, 'name'], null),
  [providerKey, providersCache, providerDetailsCache]);

  useEffect(() => {
    (async () => {
      try {
        if (!providerKey || provider || providersCache[providerKey]) return;
        addProviders(providerKey, {});
        const providersData = await findProviders(providerKey);
        providersData.forEach(({ key, value }) => {
          try {
            addProviders(key, JSON.parse(value));
          } catch (error) {
            console.error(error);
          }
        });
      } catch (error2) {
        console.error(error2);
      }
    })();
  }, [providerKey, provider, addProviders, providersCache]);

  const displayPrice = useMemo(() => (
    Math.max(...[price || ''].flat().filter((d, index) => index < 2)).toLocaleString('en-US')
  ), [price]);

  const displaySellPrice = useMemo(() => (
    _.uniq([sellPrice || ''].flat().filter((d, index) => index < 2)).map((d) => d.toLocaleString('en-US')).join('~')
  ), [sellPrice]);

  const determinedPrice = useMemo(() => {
    if (!specs || _.isEmpty(specs)) return _.get(price, [2], undefined);
    if (!specPrices || _.isEmpty(specPrices)) return undefined;
    const selectedSpecsKey = Object.keys(selectedSpecs).map((key) => selectedSpecs[key]).sort().join('-');
    if (specPrices[selectedSpecsKey] !== undefined) return (specPrices[selectedSpecsKey] || 0);
    return undefined;
  }, [specPrices, selectedSpecs, price, specs]);

  const determinedSellPrice = useMemo(() => {
    if (!specs || _.isEmpty(specs)) return _.get(sellPrice, [2], determinedPrice);
    if (!specSellPrices || _.isEmpty(specSellPrices)) return determinedPrice;
    const selectedSpecsKey = Object.keys(selectedSpecs).map((key) => selectedSpecs[key]).sort().join('-');
    if (specSellPrices[selectedSpecsKey] !== undefined) {
      return (specSellPrices[selectedSpecsKey] || 0);
    }
    return determinedPrice;
  }, [specSellPrices, selectedSpecs, determinedPrice, specs, sellPrice]);

  const addToCart = useCallback(() => {
    if (!productDetailData) {
      setAlert({ title: t('ec.add_to_cart_failure'), message: t('ec.add_to_cart_failure') });
      return false;
    }
    const orderedSelectedSpecs = specs.map((spec) => {
      const {
        name: specName,
        options: specOptions,
      } = spec;
      const selectedSpec = specOptions.find((d) => d.key === selectedSpecs[specName]) || {};
      return {
        name: specName,
        key: selectedSpecs[specName],
        value: selectedSpec.name,
      };
    });
    const missingValues = orderedSelectedSpecs.filter((d) => !d.value).map((d) => d.name);
    if (!selectedAmount) missingValues.push(t('ec.select_amount'));
    if (missingValues.length > 0) {
      setAlert({ title: t('ec.please_choose_'), message: (t('ec.please_choose_') + missingValues.join(', ')) });
      return false;
    }
    for (let i = 0; i < selectedAmount; i += 1) {
      addItem({
        ...productDetailData,
        key: productKey,
        provider,
        providerKey,
        specs: orderedSelectedSpecs,
        sellPrice: determinedSellPrice,
      });
    }
    setAlert({ title: t('ec.add_to_cart_success'), message: t('ec.add_to_cart_success') });

    return true;
  }, [productKey, selectedAmount, selectedSpecs, productDetailData, specs, provider,
    providerKey, addItem, t, determinedSellPrice]);

  const checkout = useCallback(() => {
    if (!addToCart()) return;
    history.push('/ec/cart');
  }, [addToCart, history]);

  useEffect(() => {
    if (productDetailData) return;
    (async () => {
      try {
        startProgress();
        const data = (await findProductDetails([productKey])) || [];
        data.forEach(({ key, value }) => {
          try {
            addProductDetails(key, JSON.parse(value));
          } catch (error) {
            console.error(error);
          }
        });
      } catch (error) {
        console.error(error);
      }
      endProgress();
    })();
  }, [productKey, productDetailData, startProgress, endProgress, addProductDetails]);

  const [productStock, setProductStock] = useState({});
  useEffect(() => {
    if (!productKey) return;
    (async () => {
      try {
        const data = (await findProductStock([productKey])) || [];
        setProductStock(data.reduce((result, current) => (
          {
            ...result,
            [current.key]: current.value,
          }
        ), {}));
      } catch (error) {
        console.error(error);
      }
    })();
  }, [productKey]);

  const [displayTag, setDisplayTag] = useState(null);
  useEffect(() => {
    if (!defaultTag || !providerKey || displayTag) return;
    if (providerTagsCache[providerKey]) {
      let foundTag = null;
      const foundParentTag = (providerTagsCache[providerKey] || []).find((tagParent) => {
        foundTag = ((tagParent.children || []).find((tag) => tag.key === defaultTag) || null);
        return foundTag;
      }) || null;
      if (foundTag && foundParentTag) {
        setDisplayTag([foundParentTag, foundTag]);
      }
    } else {
      (async () => {
        try {
          const providerTagsData = await findProviderTags(providerKey);
          const providerTagDictionary = {};
          const providerTags = [];

          providerTagsData.forEach((row) => {
            const {
              key,
            } = row;
            providerTagDictionary[key] = row;
          });
          providerTagsData.forEach((row) => {
            const {
              parent_tag,
            } = row;
            if (!parent_tag) {
              providerTags.push(row);
            } else {
              const parentTag = providerTagDictionary[parent_tag];
              if (parentTag) {
                parentTag.children = [
                  ...(parentTag.children || []),
                  row,
                ];
              }
            }
          });

          addProviderTags(providerKey, providerTags);
        } catch (error) {
          console.error(error);
        }
      })();
    }
  }, [defaultTag, providerKey, displayTag, providerTagsCache, addProviderTags]);
  const displayTagComponent = useMemo(() => [
    <Link to="/">{config.BrandNameTW}</Link>,
    provider ? <Link to={`/ec/search/${providerKey}`}>{provider}</Link> : null,
    _.get(displayTag, [0, 'value'], ''),
    _.get(displayTag, [1, 'value'], ''),
  ].filter((d) => d).map((d, index) => [index ? '  >  ' : null, d]),
  [provider, providerKey, displayTag]);

  const sanitizedDescriptionContent = useMemo(() => DOMPurify.sanitize(
    _.get(descriptions, [selectedDescriptionIndex, 'content'], ''),
  ),
  [descriptions, selectedDescriptionIndex]);

  useEffect(() => {
    const stockKey = Object.keys(selectedSpecs).map((key) => selectedSpecs[key]).sort().join('-');
    const stock = productStock[stockKey];
    if (!stock) {
      setSelectedAmount(0);
    } else if (selectedAmount < 1) {
      setSelectedAmount(1);
    } else if (selectedAmount > stock) {
      setSelectedAmount(stock);
    }
  }, [selectedSpecs, selectedAmount, productStock]);
  return (
    <>
      <Navmenu />
      <ProductDetailPageContainer>
        <TagSetContainer>
          {displayTagComponent}
        </TagSetContainer>
        <ImageSetContainer>
          <ImageSet images={images} isPrepared={isPrepared} />
        </ImageSetContainer>
        <DetailContainer>
          {
            isPrepared ? (
              <>
                <ProviderName>{provider || '_'}</ProviderName>
                <Name>{name}</Name>
                {(() => {
                  const finalPrice = (
                    determinedPrice !== undefined
                      ? determinedPrice.toLocaleString('en-US') : displayPrice
                  );
                  const finalSellPrice = (
                    determinedSellPrice !== undefined
                      ? determinedSellPrice.toLocaleString('en-US')
                      : displaySellPrice
                  );
                  if (finalPrice === finalSellPrice) {
                    return (
                      <Price>
                        $
                        {finalPrice}
                      </Price>
                    );
                  }
                  return (
                    <>
                      <PriceLineThrough>
                        $
                        {finalPrice}
                      </PriceLineThrough>
                      <SellPrice>
                        $
                        {finalSellPrice}
                      </SellPrice>
                    </>
                  );
                })()}
                <SpecContainer>
                  {
                    (!specs || _.isEmpty(specs)) && (
                      <tr key="1">
                        <td>{t('ec.spec')}</td>
                        <td>
                          <Select value="1">
                            <option
                              key="1"
                              value="1"
                              aria-label="None"
                              label={`${t('ec.uni_spec')}${
                                (productStock[''] === undefined) ? '' : (
                                  ` (${t('ec.stock')}:${productStock['']})`
                                )
                              }`}
                            />
                          </Select>
                        </td>
                      </tr>
                    )
                  }
                  {
                    (specs || []).map(({
                      name: specName,
                      options,
                    }, index) => (
                      <tr key={specName}>
                        <td>{specName}</td>
                        <td>
                          {
                            (index === 0 && specs.length > 1) ? (
                              (options || []).map((option) => (
                                <SpecOption
                                  key={option.key}
                                  selected={_.get(selectedSpecs, [specName], null) === option.key}
                                  onClick={() => setSelectedSpecs({
                                    ...selectedSpecs,
                                    [specName]: option.key,
                                  })}
                                >
                                  {option.name}
                                </SpecOption>
                              ))
                            ) : (
                              <Select
                                value={_.get(selectedSpecs, [specName], '')}
                                onChange={(e) => setSelectedSpecs({
                                  ...selectedSpecs,
                                  [specName]: e.target.value,
                                })}
                              >
                                <option key="" value="" aria-label="None" label="" />
                                {
                                  (() => {
                                    const partialStockKey = Object.keys(selectedSpecs)
                                      .filter((d) => d !== specName)
                                      .map((key) => selectedSpecs[key]);
                                    return (options || []).map((option) => {
                                      const stockKey = [
                                        ...partialStockKey,
                                        option.key,
                                      ].sort().join('-');
                                      const stock = productStock[stockKey];
                                      const stockString = (stock === undefined) ? '' : (
                                        ` (${t('ec.stock')}:${stock})`
                                      );
                                      return (
                                        <option
                                          key={option.key}
                                          value={option.key}
                                          label={`${option.name}${stockString}`}
                                          disabled={!stock}
                                        />
                                      );
                                    });
                                  })()
                                }
                              </Select>
                            )
                          }
                        </td>
                      </tr>
                    ))
                  }
                  <tr>
                    <td>{t('ec.select_amount')}</td>
                    <td>
                      <AmountSelector>
                        <div id="decrease" onClick={() => setSelectedAmount((i) => Math.max(1, i - 1))}>-</div>
                        <div id="amount">{selectedAmount}</div>
                        <div id="increase" onClick={() => setSelectedAmount((i) => Math.min(20, i + 1))}>+</div>
                      </AmountSelector>
                      <AddToCartLink onClick={addToCart}>
                        {t('ec.add_to_cart')}
                      </AddToCartLink>
                    </td>
                  </tr>
                </SpecContainer>
                <CheckoutButton tabIndex={0} onClick={checkout}>{t('ec.checkout')}</CheckoutButton>
              </>
            ) : (
              <>
                <Skeleton variant="text" animation="wave" width="30%" />
                <Skeleton variant="text" animation="wave" />
                <Skeleton variant="text" animation="wave" width="50%" />
                <Skeleton variant="text" animation="wave" width="50%" />
                <Skeleton variant="text" animation="wave" width="50%" />
                <Skeleton variant="text" animation="wave" />
                <Skeleton variant="text" animation="wave" />
              </>
            )
          }
        </DetailContainer>
        <DescriptionTagContainer>
          {
            (descriptions || []).map(({ name: descriptionName }, index) => (
              <DescriptionTag
                key={descriptionName}
                onClick={() => setSelectedDescriptionIndex(index)}
                selected={index === selectedDescriptionIndex}
              >
                {descriptionName || ''}
              </DescriptionTag>
            ))
          }
        </DescriptionTagContainer>
        {
          isPrepared
            ? (
              <DescriptionContainer
                dangerouslySetInnerHTML={{ __html: sanitizedDescriptionContent }}
              />
            )
            : (
              <>
                <Skeleton variant="text" animation="wave" />
                <Skeleton variant="text" animation="wave" />
                <Skeleton variant="text" animation="wave" />
                <Skeleton variant="text" animation="wave" />
                <Skeleton variant="text" animation="wave" />
                <Skeleton variant="text" animation="wave" width="50%" />
              </>
            )
        }

      </ProductDetailPageContainer>
      {alert && (
        <Alert
          isOpen
          title={alert?.title || ''}
          content={alert?.message || ''}
          buttons={alert?.buttons || (
            <Button fullWidth onClick={() => setAlert(undefined)}>
              {t('ec.confirm')}
            </Button>
          )}
        />
      )}
    </>
  );
};
